import FormWithProvider from 'components/forms/form/Form';
import useAppModal from 'hooks/useAppModal';
import { LanguageCodeUnderscored } from 'i18n/internationalization.types';
import { PropertyOwner, PropertyOwnerPermissionType } from 'models/Owner';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useFetchAgency from 'components/domain/agency/useFetchAgency';
import useAppUser from 'hooks/useAppUser';
import Loader from 'components/loader/Loader';
import CommissionFields from './CommissionFields';
import LinkExistingOwner from './LinkExistingOwner';
import { ModalContainer } from './OwnerModal.styles';
import OwnerProfileFields from './OwnerProfileFields';
import { formSchema, OwnerFormSchemaType } from './OwnerModal.schema';
import OwnerModalFooter from './OwnerModalFooter';

export const ownerModalId = 'owner-modal';

const OwnerModal = ({
  owner,
  onSubmit,
}: {
  owner?: PropertyOwner;
  onSubmit: (values: OwnerFormSchemaType) => void;
}) => {
  const isEdit = !!owner;
  const { t } = useTranslation();
  const { closeModal } = useAppModal();

  const { agencyUid } = useAppUser();
  const { data: agency, isLoading: isLoadingAgency } = useFetchAgency({
    agencyUid: agencyUid as string,
  });

  const getDefaultValues = (): OwnerFormSchemaType => {
    if (isEdit) {
      const phoneNumberAreaCode = owner?.phoneNumberAreaCode
        ? `+${owner?.phoneNumberAreaCode}`
        : '+1';

      return {
        owner: {
          businessName: owner?.businessName ?? '',
          ccEmails: owner?.ccEmails ?? '',
          email: owner?.email,
          firstName: owner?.firstName ?? '',
          lastName: owner?.lastName ?? '',
          phoneNumber: `${phoneNumberAreaCode}${owner?.phoneNumber ?? ''}`,
          phoneNumber_areaCode: phoneNumberAreaCode,
          phoneNumber_base: owner?.phoneNumber ?? '',
          preferredLocale:
            owner?.preferredLocale ?? LanguageCodeUnderscored.EN_US,
          agencyCommissionRate: owner?.agencyCommissionRate ?? 0,
          permissionTypes: owner?.permissionTypes ?? null,
          pictureUrl: owner?.pictureUrl ?? null,
          uid: owner?.uid ?? '',
        },
        linkedOwnerUid: '',
      };
    }

    return {
      owner: {
        uid: null,
        email: '',
        firstName: '',
        lastName: '',
        businessName: '',
        phoneNumber: '+1',
        phoneNumber_areaCode: '+1',
        phoneNumber_base: '',
        preferredLocale: LanguageCodeUnderscored.EN_US,
        agencyCommissionRate: 0,
        ccEmails: '',
        permissionTypes: [PropertyOwnerPermissionType.CREATING_BLOCKS],
        pictureUrl: null,
      },
      linkedOwnerUid: '',
    };
  };

  const handleClose = () => {
    closeModal(ownerModalId);
  };

  if (isLoadingAgency) {
    return <Loader />;
  }

  return (
    <FormWithProvider
      horizontal
      noValidate
      onSubmit={onSubmit}
      defaultValues={getDefaultValues()}
      resolver={formSchema}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit
            ? t('pageProperty.owners.modal.editTitle')
            : t('pageProperty.owners.modal.addTitle')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalContainer>
          {!isEdit && <LinkExistingOwner />}

          <OwnerProfileFields
            isEdit={isEdit}
            agencyCountryCode={agency?.countryCode}
          />

          <CommissionFields />
        </ModalContainer>
      </Modal.Body>

      <OwnerModalFooter isEdit={isEdit} onClose={handleClose} />
    </FormWithProvider>
  );
};

OwnerModal.defaultProps = {
  owner: undefined,
};

export default OwnerModal;
